import React, { useState } from 'react';
import styled from 'styled-components';

import Avatar from '@components/CRM/Clients/Avatar';

import { B2BClient } from './types';

// Приглушённые фоны: тёмные инициалы на них читаются, и ни один не выбивается из остальных.
const AVATAR_COLORS = [
  '#e3e8ef',
  '#e5ece3',
  '#efe8dd',
  '#ece5ef',
  '#e1ecee',
  '#f0e6e4',
  '#e8e7e0',
  '#e3e6f1',
];

// Цвет зависит только от id клиента: у одного клиента он одинаковый на любой странице и после перезагрузки.
export const getClientAvatarColor = (clientId: string): string => {
  const hash = Array.from(clientId).reduce(
    (acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0,
    0,
  );

  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// Сначала фамилия, потом имя — как в полном имени клиента.
export const getClientInitials = ({
  name,
  surname,
}: Pick<B2BClient, 'name' | 'surname'>): string =>
  [surname, name]
    .map((part) => (part || '').trim().charAt(0))
    .join('')
    .toUpperCase();

interface ClientAvatarProps {
  className?: string;
  client: Pick<B2BClient, 'id' | 'name' | 'surname' | 'fullName'>;
  // Настоящее фото клиента; без него или если оно не загрузилось — инициалы.
  url?: string | null;
}

const ClientAvatar: React.FC<ClientAvatarProps> = ({
  className,
  client,
  url = null,
}) => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const initials = getClientInitials(client);

  if (url && url !== failedUrl) {
    return (
      <Photo
        className={className}
        src={url}
        alt={client.fullName}
        title={client.fullName}
        onError={() => setFailedUrl(url)}
      />
    );
  }

  // Без имени и фамилии инициалов нет — общая заглушка.
  if (!initials) {
    return <Avatar className={className} name={client.fullName} url={null} />;
  }

  return (
    <Initials
      className={className}
      title={client.fullName}
      $color={getClientAvatarColor(client.id)}
    >
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle">
          {initials}
        </text>
      </svg>
    </Initials>
  );
};

const Photo = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 40px;
  object-fit: cover;
  width: 40px;
`;

// Инициалы в SVG масштабируются вместе с аватаркой, какой бы размер ей ни задали снаружи.
const Initials = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: 50%;
  color: #4a5260;
  flex-shrink: 0;
  height: 40px;
  width: 40px;

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }

  text {
    fill: currentColor;
    font-size: 15px;
    font-weight: 600;
  }
`;

export default ClientAvatar;
